// @ts-nocheck
import { isRejectedWithValue } from "@reduxjs/toolkit";
import type { MiddlewareAPI, Middleware } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";
import { ErrorCard } from "@repo/ping-design";

const TOAST_STYLES = {
  minWidth: "300px",
};

const resetErrorDisplayed = debounce(() => {
  // only displace single error when backend goes down
  window.hasDisplayedApiError = false;
}, 3000);

export const rtkQueryErrorLogger: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    if (isRejectedWithValue(action)) {
      const status = action.payload?.status;
      const data = action.payload?.data;
      // Quota/rate-limit responses are rendered inline by the calling panel.
      // Do not obscure that message with a generic global toast.
      if (status === 429) {
        return next(action);
      }
      const isFieldValidationError =
        status === 400 && !data?.detail && !data?.error && !data?.message;

      if (!isFieldValidationError && !window.hasDisplayedApiError) {
        let errorMsg = "";
        if (action.payload?.originalStatus === 500) {
          errorMsg =
            "A server error has occurred. Our development team has been notified. Please try again later.";
        } else {
          errorMsg =
            action.payload?.data?.detail ||
            action.payload?.data?.error ||
            action.payload?.data?.message ||
            "There was a problem with your request.";
        }

        toast.error(errorMsg, {
          duration: 2000,
          style: TOAST_STYLES,
        });

        window.hasDisplayedApiError = true;
        resetErrorDisplayed();
      }
    }

    return next(action);
  };

// List of "errors" which are expected behavior which we don't want to display (i.e. 404)
// Without payload_message, every error from a matching url is bypassed.
const bypassedErrors = [
  {
    url_includes: "/v1/pli/building_outlines",
    payload_message: "Failed to get parcel id",
  },
  {
    // A Location link's pliId the user can't open; Location falls back to a new PLI.
    url_includes: "check_pli_access=1",
  },
];
export const rtkQueryMapsErrorLogger: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    if (isRejectedWithValue(action)) {
      //Automatically bypass error if it matches urls and payload_message
      for (const item of bypassedErrors) {
        const matching_url = (
          action?.meta?.baseQueryMeta?.request?.url ?? ""
        ).includes(item.url_includes);
        const matching_error =
          item.payload_message === undefined ||
          (action?.payload?.data?.detail ?? "") == item.payload_message;
        if (matching_url && matching_error) {
          return next(action);
        }
      }

      const status = action.payload?.status;
      const data = action.payload?.data;
      // Quota/rate-limit responses are rendered inline by the calling panel.
      if (status === 429) {
        return next(action);
      }
      const isFieldValidationError =
        status === 400 && !data?.detail && !data?.error && !data?.message;

      if (!isFieldValidationError && !window.hasDisplayedApiError) {
        let errorMsg = "";
        if (action.payload?.originalStatus === 500) {
          const didViewOnlyLinkExpire = action.payload.data
            ?.toLowerCase()
            .includes("signature has expired");

          errorMsg = didViewOnlyLinkExpire
            ? "We're sorry but this link has expired."
            : "A server error has occurred. Our development team has been notified. Please try again later.";
        } else {
          errorMsg =
            action.payload?.data?.detail ||
            action.payload?.data?.error ||
            action.payload?.data?.message ||
            "There was a problem with your request.";
        }

        const currentPath = window.location.pathname;

        if (currentPath.includes("/new")) {
          if (currentPath.includes("/maps/pli")) {
            toast.custom(
              (t) => (
                <ErrorCard
                  title="Error"
                  error={errorMsg}
                  onClose={() => {
                    toast.dismiss();
                  }}
                  variant="border"
                />
              ),
              { duration: 2000, style: TOAST_STYLES, position: "top-center" },
            );
          } else {
            toast.custom(
              (t) => (
                <div style={{ position: "relative", top: "44px" }}>
                  <ErrorCard
                    title="Error"
                    error={errorMsg}
                    onClose={() => {
                      toast.dismiss();
                    }}
                    variant="border"
                  />
                </div>
              ),
              { duration: 2000, style: TOAST_STYLES, position: "top-center" },
            );
          }
        } else {
          toast.error(errorMsg, {
            duration: 2000,
            style: TOAST_STYLES,
          });
        }

        window.hasDisplayedApiError = true;
        resetErrorDisplayed();
      }
    }

    return next(action);
  };
